/**
 * InnerMirrorDpStep — "Give Your Inner Light a Face" picker.
 *
 * Three options:
 *   1. Upload Photo (JPG / PNG / WebP, ≤ 5 MB client-side)
 *   2. Use My KALKI Profile Photo  (if user.picture is a Google-hosted URL)
 *   3. Continue Without Photo
 *
 * Picked images are downscaled to a 512×512 square data URL on the client
 * so the final card canvas stays fast. A simple zoom slider lets the
 * user re-centre their face. No face detection / AI retouching is applied
 * — the directive forbids automatic facial modifications.
 *
 * Persistence: data URL kept in sessionStorage keyed by user_id so a
 * refresh preserves the DP without any server-side upload for v1.2.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, UserRound, X } from "lucide-react";
import { api } from "@/hooks/useAuth";

const MAX_BYTES = 5 * 1024 * 1024;
const OUT = 512;

function storageKey(userId) {
  return `kalki_inner_mirror_dp:${userId || "anon"}`;
}

export function loadStoredDp(userId) {
  try {
    const raw = sessionStorage.getItem(storageKey(userId));
    if (raw) return JSON.parse(raw);
  } catch (_) {}
  return null;
}

function saveStoredDp(userId, value) {
  try {
    if (value) sessionStorage.setItem(storageKey(userId), JSON.stringify(value));
    else sessionStorage.removeItem(storageKey(userId));
  } catch (_) {}
}

// Debounced server-side persistence — mirrors the sessionStorage copy so
// the Devotee Card on the recipient's `/ack/{token}` page can render the
// sender's photo. Fire-and-forget: a DP sync failure must not block the
// local experience. Uses an in-module token to drop stale calls.
let _lastDpSyncToken = 0;
let _lastDpSyncPayload = "";
function syncDpToServer(dataUrl, publicInclude) {
  const payload = `${publicInclude ? 1 : 0}|${dataUrl || ""}`;
  if (payload === _lastDpSyncPayload) return;
  _lastDpSyncPayload = payload;
  const me = ++_lastDpSyncToken;
  setTimeout(() => {
    if (me !== _lastDpSyncToken) return;
    api.post("/inner-mirror/dp", {
      data_url: dataUrl || null,
      public_include: !!publicInclude,
    }).catch(() => { /* silent — local copy still works */ });
  }, 350);
}

/** Convert an image source (file-blob or URL) to a 512×512 center-cropped
 *  square data URL at the chosen zoom (1 = fits short edge). */
function squareImage(img, zoom = 1) {
  const canvas = document.createElement("canvas");
  canvas.width = OUT; canvas.height = OUT;
  const ctx = canvas.getContext("2d");
  const short = Math.min(img.naturalWidth, img.naturalHeight) / zoom;
  const sx = (img.naturalWidth - short) / 2;
  const sy = (img.naturalHeight - short) / 2;
  // White background so transparent PNGs don't bleed black.
  ctx.fillStyle = "#FFFFFF";
  ctx.fillRect(0, 0, OUT, OUT);
  ctx.drawImage(img, sx, sy, short, short, 0, 0, OUT, OUT);
  return canvas.toDataURL("image/png", 0.92);
}

export default function InnerMirrorDpStep({ userId, userPicture, value, onChange }) {
  const [raw, setRaw] = useState(null); // HTMLImageElement before cropping
  const [zoom, setZoom] = useState(1);
  const [publicInclude, setPublicInclude] = useState(value?.publicInclude ?? true);
  const fileRef = useRef(null);

  // Re-crop whenever zoom changes.
  useEffect(() => {
    if (!raw) return;
    const cropped = squareImage(raw, zoom);
    onChange({ dataUrl: cropped, zoom, publicInclude });
    saveStoredDp(userId, { dataUrl: cropped, zoom, publicInclude });
    syncDpToServer(cropped, publicInclude);
  }, [raw, zoom, publicInclude, onChange, userId]);

  const onFile = useCallback(async (file) => {
    if (!file) return;
    if (!/^image\/(jpeg|png|webp)$/.test(file.type)) {
      toast.error("Please pick a JPG, PNG or WebP photo.");
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error("Please pick a photo under 5 MB.");
      return;
    }
    const url = URL.createObjectURL(file);
    try {
      const img = await new Promise((resolve, reject) => {
        const i = new Image();
        i.onload = () => resolve(i);
        i.onerror = reject;
        i.src = url;
      });
      setRaw(img);
      setZoom(1);
    } catch (_) {
      toast.error("That image could not be loaded.");
    } finally {
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    }
  }, []);

  const useProfilePhoto = useCallback(async () => {
    if (!userPicture) {
      toast.error("Your account does not have a profile photo yet.");
      return;
    }
    try {
      const img = await new Promise((resolve, reject) => {
        const i = new Image();
        // Googleusercontent allows CORS; if the host blocks it we fall
        // back to a direct <img> and skip squaring.
        i.crossOrigin = "anonymous";
        i.onload = () => resolve(i);
        i.onerror = reject;
        i.src = userPicture;
      });
      setRaw(img);
      setZoom(1);
    } catch (_) {
      // CORS blocked — pass the URL through directly; InnerMirrorCard
      // will handle a URL just like a data URL.
      onChange({ dataUrl: userPicture, zoom: 1, publicInclude, isUrl: true });
      saveStoredDp(userId, { dataUrl: userPicture, zoom: 1, publicInclude, isUrl: true });
      syncDpToServer(userPicture, publicInclude);
      toast.success("Profile photo added.");
    }
  }, [userPicture, publicInclude, onChange, userId]);

  const removePhoto = useCallback(() => {
    setRaw(null); setZoom(1);
    onChange(null);
    saveStoredDp(userId, null);
    syncDpToServer(null, publicInclude);
  }, [onChange, userId, publicInclude]);

  const skip = useCallback(() => {
    removePhoto();
    toast.message("Continuing without a photo.");
  }, [removePhoto]);

  const hasDp = !!value?.dataUrl;

  return (
    <section className="im-dp-step" data-testid="im-dp-step">
      <div className="im-eyebrow" style={{ textAlign: "left" }}>
        ✨ Give Your Inner Light a Face
      </div>
      <p className="im-sub" style={{ margin: "6px 0 14px" }}>
        Add your photo to make this reflection uniquely yours.
      </p>
      <div className="im-dp-actions">
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          data-testid="im-dp-file"
          onChange={(e) => onFile(e.target.files?.[0])}
          style={{ display: "none" }}
        />
        <button type="button" className="im-primary"
                data-testid="im-dp-upload"
                onClick={() => fileRef.current?.click()}>
          <Upload size={14} /> Upload Photo
        </button>
        {userPicture && (
          <button type="button" className="im-ghost"
                  data-testid="im-dp-use-profile"
                  onClick={useProfilePhoto}>
            <UserRound size={14} /> Use My Profile Photo
          </button>
        )}
        <button type="button" className="im-ghost"
                data-testid="im-dp-skip"
                onClick={skip}>
          Continue Without Photo
        </button>
      </div>
      {hasDp && (
        <div className="im-dp-preview" data-testid="im-dp-preview">
          <div className="im-dp-circle">
            <img src={value.dataUrl} alt="Your card photo preview" />
          </div>
          {!value.isUrl && (
            <label className="im-dp-zoom">
              <span>Zoom</span>
              <input type="range" min="1" max="2.4" step="0.05"
                     value={zoom}
                     onChange={(e) => setZoom(parseFloat(e.target.value))}
                     data-testid="im-dp-zoom-range" />
            </label>
          )}
          <label className="im-dp-public">
            <input type="checkbox"
                   data-testid="im-dp-public-toggle"
                   checked={publicInclude}
                   onChange={(e) => setPublicInclude(e.target.checked)} />
            <span>Include my photo on the public card I share</span>
          </label>
          <button type="button" className="im-ghost im-ghost-sm"
                  data-testid="im-dp-remove"
                  onClick={removePhoto}>
            <X size={12} /> Remove photo
          </button>
        </div>
      )}
      <style>{`
        .im-dp-step { margin-top: 22px; padding: 20px;
                       background: var(--kalki-snippet-surface);
                       border: 1px solid var(--kalki-snippet-border);
                       border-radius: 20px; }
        .im-dp-actions { display: flex; gap: 10px; flex-wrap: wrap; }
        .im-dp-preview { margin-top: 18px; display: flex; flex-direction: column;
                          align-items: center; gap: 10px; }
        .im-dp-circle { width: 140px; height: 140px; border-radius: 50%;
                         overflow: hidden; border: 3px solid transparent;
                         background: linear-gradient(#FFFFFF, #FFFFFF) padding-box,
                                     linear-gradient(135deg, #5D00FF, #00C2CB) border-box;
                         box-shadow: 0 20px 50px rgba(93,0,255,0.18); }
        .im-dp-circle img { width: 100%; height: 100%; object-fit: cover;
                             display: block; }
        .im-dp-zoom { display: flex; align-items: center; gap: 12px;
                       font-size: 11px; letter-spacing: 0.14em; color: #676773;
                       text-transform: uppercase; }
        .im-dp-zoom input { width: 160px; accent-color: #5D00FF; }
        .im-dp-public { display: flex; align-items: center; gap: 8px;
                         font-size: 12px; color: #1C1C1C; }
      `}</style>
    </section>
  );
}
